'use client';

import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  IconButton,
  MetaDefault,
  SettingsRow,
} from '@terra/ui-design-system';
import { Delete02Icon, Edit03Icon, Key01Icon, PlayIcon } from '@terra/ui-icons';
import type {
  McpIdentityConnectionStatusMap,
  McpServerIdentityDto,
} from '@terra/shared-contracts-mcp-servers';
import {
  getMcpCredentialAuthTypeLabel,
  isOAuthMcpCredentialType,
} from '@terra/ui-ai-applications';

type McpServerIdentityRowProps = {
  identity: McpServerIdentityDto;
  connectionStatus: McpIdentityConnectionStatusMap[string] | null;
  isReadOnly?: boolean;
  canDelete: boolean;
  isTestDisabled: boolean;
  isAuthorizing: boolean;
  onEdit: (identity: McpServerIdentityDto) => void;
  onTest?: (identityId: string) => void;
  onAuthorize: (identity: McpServerIdentityDto) => void;
  onDelete: (identity: McpServerIdentityDto) => void;
};

export function McpServerIdentityRow({
  identity,
  connectionStatus,
  isReadOnly,
  canDelete,
  isTestDisabled,
  isAuthorizing,
  onEdit,
  onTest,
  onAuthorize,
  onDelete,
}: McpServerIdentityRowProps) {
  const t = useTranslations('aiApplications.mcpIdentities');
  const tAi = useTranslations('aiApplications');
  const tAdminMcp = useTranslations('settings.mcpServerForm');
  const tCommon = useTranslations('common');
  const authLabel = getMcpCredentialAuthTypeLabel({
    credentialType: identity.type,
    translate: tAdminMcp,
  });

  return (
    <SettingsRow
      label={identity.label}
      valueWrap
      value={
        <VStack alignItems="flex-end" gap={1} w="full">
          <HStack gap={2} alignItems="center" flexWrap="wrap">
            <span>{authLabel}</span>
            {identity.isDefault ? (
              <Badge variant="fill">{t('defaultBadge')}</Badge>
            ) : null}
            {identity.scopes && identity.scopes.length > 0 ? (
              <Badge variant="outline">{identity.scopes.join(', ')}</Badge>
            ) : null}
            {connectionStatus ? (
              connectionStatus.connectionSuccess ? (
                <Badge variant="outline" colorPalette="green">
                  {t('connectionConnected')}
                </Badge>
              ) : (
                <Badge variant="outline" colorPalette="red">
                  {t('connectionFailed')}
                </Badge>
              )
            ) : null}
          </HStack>
          {connectionStatus &&
          !connectionStatus.connectionSuccess &&
          connectionStatus.error ? (
            <MetaDefault
              color="fg.error"
              textAlign="right"
              wordBreak="break-word"
            >
              {connectionStatus.error}
            </MetaDefault>
          ) : null}
        </VStack>
      }
      action={
        isReadOnly
          ? undefined
          : {
              label: tCommon('edit'),
              icon: <Edit03Icon size={16} />,
              onClick: () => onEdit(identity),
            }
      }
      control={
        !isReadOnly ? (
          <HStack gap={1}>
            {onTest ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={tAi('actions.testConnection')}
                onClick={() => onTest(identity.id)}
                disabled={isTestDisabled}
              >
                <PlayIcon size={16} />
              </IconButton>
            ) : null}
            {isOAuthMcpCredentialType(identity.type) ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={tAdminMcp('oauthAuthorize')}
                onClick={() => onAuthorize(identity)}
                disabled={isAuthorizing}
              >
                <Key01Icon size={16} />
              </IconButton>
            ) : null}
            {canDelete ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={tCommon('delete')}
                onClick={() => onDelete(identity)}
              >
                <Delete02Icon size={16} />
              </IconButton>
            ) : null}
          </HStack>
        ) : undefined
      }
    />
  );
}
